import { useEffect, useMemo, useState } from "react";
import { Button, ButtonProps } from "../../buttons/Button/Button";
import { EntityConstants, IGenericRequestParameters, InferSortByOptions } from "../../../api/GenericTypes";
import { IconNames, LucideIcon } from "../../icon/LucideIcon";
import { PdfSettings, useExportPDF } from "./useExportPdf";
import { PdfExportSettingsModal } from "../../../Entities/GenericComponents/common/GenericEntityExportButton";
import { GenericEntity } from "../../../Entities/GenericComponents/GenericComponents";
import { fileExportJobsConstants } from "../../../api/FileExportJobs";

interface ExportPdfButtonProps<
  Entity extends GenericEntity,
  Filters extends { [property: string]: any } & IGenericRequestParameters<Entity, InferSortByOptions<Filters>>
> extends Omit<ButtonProps, "onClick" | "children"> {
  loading?: boolean;
  entityConstants: EntityConstants;
  filters?: Filters;
  defaultPdfSettings?: PdfSettings;
  skipPdfSettingsModal?: boolean;
  label?: string;
  icon?: IconNames;
}

export const ExportPdfButton = <
  Entity extends GenericEntity,
  Filters extends { [property: string]: any } & IGenericRequestParameters<Entity, InferSortByOptions<Filters>>
>({
  loading,
  disabled,
  entityConstants,
  filters,
  defaultPdfSettings: defaultPdfSettingsProp,
  className = "btn btn btn-sm btn-ghost-secondary",
  skipPdfSettingsModal = false,
  label = "Export to PDF",
  title,
  icon = fileExportJobsConstants.icon,
  ...rest
}: ExportPdfButtonProps<Entity, Filters>) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const defaultPdfSettings: PdfSettings = useMemo(() => {
    var fileName = !!defaultPdfSettingsProp?.fileName?.length
      ? defaultPdfSettingsProp.fileName
      : entityConstants.entityPlural;
    return {
      outputType: "Separate",
      separator: "PageBreak",
      ...defaultPdfSettingsProp,
      fileName: fileName.trim().replaceAll(/[^a-zA-Z0-9_-]/g, "_"),
    };
  }, [defaultPdfSettingsProp, entityConstants.entityPlural]);

  const [pdfSettings, setPdfSettings] = useState<PdfSettings>(defaultPdfSettings);
  useEffect(() => {
    !isOpen && setPdfSettings(defaultPdfSettings);
  }, [defaultPdfSettings, isOpen]);

  const { exportToPdf, loading: preparingExport } = useExportPDF<Entity, Filters>({
    entityConstants,
    filters,
    pdfSettings,
  });

  return (
    <>
      <Button
        title={title ?? `Export ${entityConstants.entityPlural} to PDF`}
        loading={loading}
        className={className}
        {...rest}
        disabled={preparingExport || disabled}
        onClick={() => (skipPdfSettingsModal ? exportToPdf() : setIsOpen(true))}
      >
        <LucideIcon name={icon} /> {label}
      </Button>
      {isOpen && !skipPdfSettingsModal && (
        <PdfExportSettingsModal
          showModal={isOpen}
          setShowModal={setIsOpen}
          pdfSettings={pdfSettings}
          setPdfSettings={setPdfSettings}
          defaultPdfSettings={defaultPdfSettings}
          handleExport={exportToPdf}
          entityConstants={entityConstants}
          isMultipleEntities
        />
      )}
    </>
  );
};
